import React from 'react';
import { useEffect, useState } from 'react';
import {
	Button, H2, Label, ShipmentStatusChip, SkeletonLoader, Spacer, Text, Tile
} from '@components';
import {
	APICallProps, BUTTON_ICON_POS, BUTTON_TYPES, CARGO_TYPE_ID, CHIP_SIZE, SPACER_DIRECTION, TChipStatus, TEOSLoadMasterManifest, TETAReason, TShipmentCargo, YourLoadItem
} from '@types';
import { useGetETAReasons, useGetLoadMasterManifestData, useGetYourLoadsData } from '@api';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import {
	DATE_FORMATS, getCargoFullDescription, getCargoTypeLabel, parseAndFormatDate, rateCardTypeFromCargoTypeId
} from '@utils';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ChevronRight } from '@mui/icons-material';
import styles from './MobileETAUpdate.module.scss';
import { useWithImpersonation } from '@hooks';
import ETAStop from './ETAStop/ETAStop';

type TMobileETAUpdateProps = {
	setIsBackArrowVisible: React.Dispatch<React.SetStateAction<boolean>>;
	isBackArrowClicked: boolean;
	setIsBackArrowClicked: React.Dispatch<React.SetStateAction<boolean>>;
	loadToUpdateETA: YourLoadItem | null;
}

export const MobileETAUpdate = (props: TMobileETAUpdateProps) => {
	const {
		setIsBackArrowVisible,
		isBackArrowClicked,
		setIsBackArrowClicked,
		loadToUpdateETA,
	} = props;
	const withImpersonation = useWithImpersonation('carrier');
	const { callGetYourLoadData } = useGetYourLoadsData();
	const [showLoadList, setShowList] = useState<boolean>(true);
	const [loads, setLoads] = useState<YourLoadItem[]>([]);
	const [currentLoad, setCurrentLoad] = useState<YourLoadItem | null>(null);
	const [manifest, setManifest] = useState<TEOSLoadMasterManifest | null>(null);
	const [pickup, setPickup] = useState<TEOSLoadMasterManifest['stops'][string] | null>(null);
	const [dropoff, setDropoff] = useState<TEOSLoadMasterManifest['stops'][string] | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [ETAReasons, setETAReasons] = useState<TETAReason[]>([]);
	const { callGetLoadMasterManifestData } = useGetLoadMasterManifestData();
	const { callGetETAReasonsData } = useGetETAReasons();

	const handleOnLoadClick = (load: YourLoadItem) => {
		setCurrentLoad(load);
	}

	const fetchLoadMasterManifest = (loadId: string) => {
		setIsLoading(true);
		setShowList(false);
		callGetLoadMasterManifestData({ routes: [loadId] })
			.then((res) => {
				if (res?.data) {
					setManifest(res.data?.data);
					setIsBackArrowVisible(true);
					const pickupKey = Object.keys(res.data?.data.stops).find((key) => key.includes('pickup'));
					const dropoffKey = Object.keys(res.data?.data.stops).find((key) => key.includes('dropoff'));
					const pick_up = res.data?.data.stops[pickupKey as keyof TEOSLoadMasterManifest];
					const drop_off = res.data?.data.stops[dropoffKey as keyof TEOSLoadMasterManifest];
					setPickup(pick_up)
					setDropoff(drop_off)
				}
			})
			.catch((err) => console.log(err))
			.finally(() => setIsLoading(false));
	}

	const handleViewDetailsClick = () => {
		window.location.href = withImpersonation(`/carrier/your-loads/${currentLoad?.master_manifest?.id}`);
	}

	const renderCargoDescription = (load: YourLoadItem) => {
		const cargos = load?.shipment?.cargos as TShipmentCargo[] | undefined;
		if (!cargos) return '';
		const cargoTypeId = load.shipment.cargo_type_id;
		const isCustomItemCargo = cargoTypeId === CARGO_TYPE_ID.CustomItem;

		return getCargoFullDescription(
			cargos,
			'pallet_quantity',
			'pallet_space_quantity',
			'weight',
			isCustomItemCargo,
			cargoTypeId
		) as string;
	}


	useEffect(() => {
		if (currentLoad) {
			fetchLoadMasterManifest(currentLoad?.master_manifest.id)
		}
	}, [currentLoad?.master_manifest.id])

	useEffect(() => {
		const fetchProps: APICallProps = {};
		fetchProps['params'] = {
			'filter[and][0][columnField]': 'status',
			'filter[and][0][operatorValue]': 'manifestStatusIs',
			'filter[and][0][value]': 'Not Started,On the Road'
		}


		callGetYourLoadData(fetchProps)
			.then((res) => {
				if (res?.data?.data) {
					setLoads(res.data.data);

				}
			})
			.catch((err) => console.log(err))
			.finally(() => setIsLoading(false))
		callGetETAReasonsData()
			.then((res) => {
				if (res?.data?.data) {
					setETAReasons(res.data.data);
				}
			})
			.catch((err) => console.log(err))
	}, []);

	useEffect(() => {
		if (isBackArrowClicked) {
			setIsBackArrowClicked(false);
			setIsBackArrowVisible(false);
			setShowList(true);
			setCurrentLoad(null);
			setManifest(null);
			setPickup(null)
			setDropoff(null)
		}
	}, [isBackArrowClicked])

	useEffect(() => {
		if (loadToUpdateETA) {
			handleOnLoadClick(loadToUpdateETA);
		}
	}, [loadToUpdateETA])

	return (
		<div>
			<div className={styles.heading}>
				<UpdateOutlinedIcon />
				<H2>Update ETA's</H2>
			</div>
			<Spacer size={8} />
			<Text>
				Not going to make the pick-up or drop-off time? You can update your ETA for loads that haven’t started or are already on the road.
			</Text>
			<Spacer size={20} />
			{
				showLoadList && isLoading && loads?.length === 0?
					<div>
						<div>
							<SkeletonLoader width='100px'/>
						</div>
						<Spacer size={8} />
						<div>
							<SkeletonLoader width='150px'/>
						</div>
					</div> : null
			}
			{
				showLoadList && loads?.length > 0 && !isLoading ?
					<>
						{
							loads.map((load: YourLoadItem) => {
								return (
									<React.Fragment key={load.id}>
										<div
											className={styles.loadContainer}
											onClick={() => handleOnLoadClick(load)}
										>
											<div className={styles.load}>
												<div className={styles.etaPanelHeading}>
													<Label>
														{(load?.shipment?.cargos as TShipmentCargo[])?.[0]?.pick_up?.suburb}
													</Label>
													<ArrowForwardIcon />
													<Label>
														{(load?.shipment?.cargos as TShipmentCargo[])?.[0]?.drop_off?.suburb}
													</Label>
												</div>
												<Text>
													{load.shipment.reference}
												</Text>
												<div className={styles.detailsRow}>
													<Text>
														{parseAndFormatDate(load.shipment.cargos[0].pickup_at, DATE_FORMATS.SHORT_DATE_NO_YEAR)}{' - '}
														{parseAndFormatDate(load.shipment.cargos[0].drop_at, DATE_FORMATS.SHORT_DATE_NO_YEAR)}
													</Text>
													<Spacer
														size={16}
														divider
														dir={SPACER_DIRECTION.VERTICAL}
														dividerColor={'var(--neutral-03)'}
													/>
													<Text>
														{getCargoTypeLabel(rateCardTypeFromCargoTypeId(load.shipment.cargo_type_id))}
													</Text>
													<Spacer
														size={16}
														divider
														dir={SPACER_DIRECTION.VERTICAL}
														dividerColor={'var(--neutral-03)'}
													/>
													<Text>
														{renderCargoDescription(load)}
													</Text>
												</div>
											</div>
											<ChevronRight />
										</div>
										<Spacer size={8} />
									</React.Fragment>

								)
							})
						}
					</> : null
			}
			{
				!showLoadList &&
				currentLoad &&
				pickup &&
				dropoff && <div>
					<Tile stretch>
						{
							isLoading ? <SkeletonLoader /> :
								<ShipmentStatusChip
									status={currentLoad?.status as TChipStatus}
									size={CHIP_SIZE.SMALL}
								/>
						}
						<Spacer size={12} />
						<div className={styles.updateETAContent}>
							{isLoading ? <SkeletonLoader /> :
								<Label>
									{`${pickup?.shipment_address?.suburb}, ${pickup?.shipment_address?.state} ${pickup?.shipment_address?.postcode}`}
								</Label>
							}
							<span className={styles.updateETAContentIcon}>
								<ArrowForwardIcon />
							</span>
							{isLoading ? <SkeletonLoader /> :
								<Label>
									{`${dropoff?.shipment_address?.suburb}, ${dropoff?.shipment_address?.state} ${dropoff?.shipment_address?.postcode}`}
								</Label>
							}
						</div>
						{isLoading ? <>
							<Spacer size={4} />
							<SkeletonLoader width='30%' />
						</> :
							<Text text={currentLoad?.shipment.reference} />
						}

						<Spacer size={4} />
						{isLoading ? <SkeletonLoader width='40%' /> :
							<Button
								type={BUTTON_TYPES.TERTIARY}
								onClick={handleViewDetailsClick}
								text={'View all load details'}
								Icon={ChevronRight}
								iconPos={BUTTON_ICON_POS.RIGHT}
							/>
						}
					</Tile>
					<Spacer size={20} />
					{
						!isLoading &&
						currentLoad &&
						manifest?.stops &&
						Object.keys(manifest?.stops).map((stop, index) => {
							return <React.Fragment key={stop}>
								<ETAStop
									load={currentLoad}
									stop={manifest?.stops[stop]}
									index={index + 1}
									onTimeUpdate={() => fetchLoadMasterManifest(`${currentLoad.master_manifest?.id}`)}
									ETAReasons={ETAReasons}
								/>
								<Spacer size={20} />
							</React.Fragment>
						})
					}
					<Spacer size={20} />
				</div>
			}
		</div>
	);
}
